import { trackUserEvent, trackError } from '@vend/utilities'
import { SaleManager } from '../../managers/sale-manager'

type EnsureSaleIsSavedResponse = {
  success: boolean
  error?: string
}

/**
 * Ensures that the current sale is saved to the database.
 * This function handles the logic of determining if the sale needs to be saved
 * and performing the save operation.
 *
 * @param saleManager {SaleManager} The sale manager instance
 * @param saleId {string|undefined} The current sale ID (if any)
 * @param saleLocalId {string} The current sale local ID
 * @return {Promise<EnsureSaleIsSavedResponse>} A promise resolved when the operation completes
 */
export const ensureSaleIsSaved = async (
  saleManager: SaleManager,
  saleId: string | undefined,
  saleLocalId: string
): Promise<EnsureSaleIsSavedResponse> => {
  try {
    // If the sale already has an ID, it's already been saved to the server
    if (saleId) {
      trackUserEvent('Web Register', 'Sale Already Saved', {
        saleId,
        saleLocalId,
      })

      return { success: true }
    }

    // Sale doesn't have an ID yet, so we need to save it
    trackUserEvent('Web Register', 'Saving Sale', {
      saleLocalId,
    })

    // NOTE: submitSale will automatically handle setting the correct sale status based on the type of fulfillment.
    // Currently, we are only supporting payment requests with fulfillment (either delivery or pickup). In the future,
    // if we want to support different types of sale statuses (layby, on account, etc.) we will need to this logic here.
    await saleManager.submitSale()

    trackUserEvent('Web Register', 'Sale Saved Successfully', {
      saleLocalId,
    })

    return { success: true }
  } catch (saveError: any) {
    const errorMessage = saveError?.message || 'Failed to save sale'

    trackError('Sale Save Error', errorMessage, {
      saleId,
      saleLocalId,
    })

    return {
      success: false,
      error: errorMessage,
    }
  }
}
